import React, { useMemo } from 'react';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

interface PasswordChecks {
  minLength: boolean;
  upper: boolean;
  lower: boolean;
  number: boolean;
  special: boolean;
}

interface PasswordStrengthResult {
  score: number; // 0-5
  percent: number; // 0-100
  label: 'Weak' | 'Fair' | 'Strong' | 'Excellent';
  color: 'error' | 'warning' | 'success';
}

const evaluatePassword = (password: string): { checks: PasswordChecks; strength: PasswordStrengthResult } => {
  const checks: PasswordChecks = {
    minLength: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };

  const passed = Object.values(checks).filter(Boolean).length;
  const percent = Math.round((passed / 5) * 100);

  let label: PasswordStrengthResult['label'] = 'Weak';
  let color: PasswordStrengthResult['color'] = 'error';

  if (percent >= 90) {
    label = 'Excellent';
    color = 'success';
  } else if (percent >= 60) {
    label = 'Strong';
    color = 'success';
  } else if (percent >= 40) {
    label = 'Fair';
    color = 'warning';
  }

  return { checks, strength: { score: passed, percent, label, color } };
};

interface PasswordRequirementsProps {
  password: string;
}

const RequirementItem: React.FC<{ met: boolean; text: string }> = ({ met, text }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {met ? (
      <CheckCircleOutlineIcon color="success" fontSize="small" />
    ) : (
      <CancelOutlinedIcon color="error" fontSize="small" />
    )}
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
);

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password }) => {
  const { checks, strength } = useMemo(() => evaluatePassword(password), [password]);

  return (
    <Stack spacing={1.25}>
      <Box>
        <LinearProgress
          variant="determinate"
          value={strength.percent}
          color={strength.color}
          sx={{ height: 8, borderRadius: 999 }}
        />
        <Typography variant="caption" color={strength.color + '.main'}>
          Strength: {strength.label}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 0 }}>
        <Stack direction="column" spacing={1} flexWrap="wrap">
          <RequirementItem met={checks.minLength} text="At least 8 characters" />
          <RequirementItem met={checks.upper} text="At least one uppercase letter" />
          <RequirementItem met={checks.lower} text="At least one lowercase letter" />
          <RequirementItem met={checks.number} text="At least one number" />
          <RequirementItem met={checks.special} text="At least one special character" />
        </Stack>
      </Paper>
    </Stack>
  );
};
